import React, { useState } from 'react';
import { ArrowUpDown, Calculator, Save, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import Tooltip from '../../../../../components/ui/Tooltip';

/**
 * Компонент формы конвертера валют
 * 
 * @param {Object} formData - Данные формы (amount, fromCurrency, toCurrency)
 * @param {Function} setFormData - Функция для обновления данных формы
 * @param {Object} currencies - Список доступных валют
 * @param {boolean} loading - Флаг загрузки
 * @param {Function} calculateConversion - Функция для расчета конвертации
 * @param {Function} saveConversion - Функция для сохранения результата конвертации
 * @param {Object} results - Результаты расчета
 */
const ConverterForm = ({ 
  formData, 
  setFormData, 
  currencies,
  loading,
  calculateConversion,
  saveConversion,
  results
}) => {
  // Состояние для отображения числовой клавиатуры на мобильных устройствах
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Обработчик изменения полей формы
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Обработчик нажатия кнопки числовой клавиатуры
  const handleCalculatorInput = (value) => {
    if (value === 'clear') {
      setFormData(prev => ({ ...prev, amount: '' }));
    } else if (value === 'backspace') {
      setFormData(prev => ({ 
        ...prev, 
        amount: prev.amount.slice(0, -1) 
      }));
    } else {
      setFormData(prev => {
        // Добавляем цифру или десятичную точку
        let newAmount = prev.amount + value;
        
        // Проверяем корректность формата числа
        if (value === '.' && prev.amount.includes('.')) {
          return prev; // Уже есть точка, не добавляем вторую
        }
        
        // Проверяем, что не начинается с нескольких нулей
        if (newAmount === '00') {
          newAmount = '0';
        }
        
        return { ...prev, amount: newAmount };
      });
    }
  };
  
  // Обмен валют местами
  const swapCurrencies = () => {
    setFormData(prev => ({
      ...prev,
      fromCurrency: prev.toCurrency,
      toCurrency: prev.fromCurrency
    }));
  };
  
  // Проверка возможности сохранения результата
  const canSaveResult = results && !loading;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center">
        <Calculator className="mr-2 h-6 w-6 text-indigo-500" />
        Конвертер валют
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Исходная сумма и валюта */}
        <div>
          <label 
            htmlFor="amount" 
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Сумма
          </label>
          <div className="flex gap-2">
            <div className="flex items-center">
              <span className="px-2 py-2 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-l-md">
                {currencies[formData.fromCurrency]?.symbol || ''}
              </span>
            </div>
            <div className="relative flex-grow">
              <input
                type="text"
                inputMode="decimal"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
                pattern="[0-9]*[.,]?[0-9]*"
                disabled={loading}
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                <label htmlFor="fromCurrency" className="sr-only">Исходная валюта</label>
                <select
                  id="fromCurrency"
                  name="fromCurrency"
                  value={formData.fromCurrency}
                  onChange={handleInputChange}
                  className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-700 dark:text-gray-300 sm:text-sm rounded-r-md focus:border-indigo-500 focus:ring-indigo-500"
                  disabled={loading}
                >
                  {Object.values(currencies).map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Tooltip text="Показать калькулятор">
              <button
                type="button"
                className="p-2.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                onClick={() => setShowCalculator(!showCalculator)}
                aria-label="Открыть калькулятор"
              >
                <Calculator className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {currencies[formData.fromCurrency]?.name || 'Валюта'}
          </p>
        </div>
        
        {/* Целевая валюта и результат */}
        <div className="flex items-start mt-8 md:mt-0">
          <div className="hidden md:flex items-center justify-center w-16 mt-2">
            <button 
              type="button"
              onClick={swapCurrencies}
              className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Поменять валюты местами"
            >
              <ArrowUpDown className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex-grow">
            <label 
              htmlFor="toCurrency" 
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Конвертировать в
            </label>
            <div className="flex">
              <span className="px-2 py-2 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-l-md">
                {currencies[formData.toCurrency]?.symbol || ''}
              </span>
              <div className="relative flex-grow">
                <input
                  type="text"
                  id="convertedAmount"
                  name="convertedAmount"
                  value={results ? (Math.round(results.convertedAmount * 10000) / 10000).toLocaleString() : ''}
                  readOnly
                  className="block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm font-medium bg-gray-50 dark:bg-gray-800"
                  placeholder="0"
                  disabled
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <label htmlFor="toCurrency" className="sr-only">Целевая валюта</label>
                  <select
                    id="toCurrency"
                    name="toCurrency"
                    value={formData.toCurrency}
                    onChange={handleInputChange}
                    className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-700 dark:text-gray-300 sm:text-sm rounded-r-md focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={loading}
                  >
                    {Object.values(currencies).map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {currencies[formData.toCurrency]?.name || 'Валюта'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Кнопка обмена валют для мобильных устройств */}
      <div className="md:hidden flex justify-center mb-6">
        <button 
          type="button"
          onClick={swapCurrencies}
          className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Поменять валюты местами"
        >
          <ArrowUpDown className="h-5 w-5" />
        </button>
      </div>
      
      {/* Всплывающая клавиатура для мобильных */}
      {showCalculator && (
        <div className="mb-6 grid grid-cols-3 gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'clear'].map(key => (
            <button
              key={key}
              type="button"
              onClick={() => handleCalculatorInput(key)}
              className={`p-3 text-center rounded ${
                key === 'clear' 
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' 
                  : 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white'
              } hover:bg-gray-200 dark:hover:bg-gray-500 font-medium transition-colors`}
            >
              {key === 'clear' ? 'C' : key}
            </button>
          ))}
        </div>
      )}
      
      <div className="flex flex-wrap gap-3">
        <Button 
          primary 
          onClick={calculateConversion}
          disabled={loading}
          icon={<RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />}
        >
          {loading ? 'Загрузка...' : 'Конвертировать'}
        </Button>
        
        <Button
          onClick={saveConversion}
          disabled={!canSaveResult}
          icon={<Save className="h-5 w-5" />}
        >
          Сохранить
        </Button>
      </div>
      
      {/* Отображение текущего курса */}
      {results && (
        <div className="mt-4 flex items-center justify-center p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
          <p className="text-sm text-indigo-700 dark:text-indigo-300">
            <span className="font-medium">Текущий курс:</span> 1 {formData.fromCurrency} = {results.exchangeRate.toFixed(4)} {formData.toCurrency}
          </p>
        </div>
      )}
    </div>
  );
};

export default ConverterForm;